// src/Dashboard/pages/user/ArbitrageJudo/ArbitrageJudoDashboard.jsx

import React, { useMemo, useState } from "react";
import {
  FaBookOpen,
  FaChevronDown,
  FaChevronUp,
  FaQuestionCircle,
} from "react-icons/fa";

import ArbitrageHero from "./components/ArbitrageHero";
import ArbitrageToolbar from "./components/ArbitrageToolbar";
import ArbitrageSectionCard from "./components/ArbitrageSectionCard";
import ArbitrageQuiz from "./components/ArbitrageQuiz";

import {
  ARBITRAGE_CATEGORIES,
  ARBITRAGE_SECTIONS,
} from "./data/arbitrageJudoData";

import { filterArbitrageSections } from "./utils/arbitrageJudoUtils";

export default function ArbitrageJudoDashboard() {
  const [activeView, setActiveView] = useState("cours"); // "cours" | "quiz"
  const [search, setSearch] = useState("");
  const [activeCategory, setActiveCategory] = useState("Tous");
  const [openSections, setOpenSections] = useState({});

  const filteredSections = useMemo(() => {
    return filterArbitrageSections(ARBITRAGE_SECTIONS, {
      search,
      category: activeCategory,
    });
  }, [search, activeCategory]);

  const allVisibleOpen = useMemo(() => {
    if (!filteredSections.length) return false;
    return filteredSections.every((section) => openSections[section.id]);
  }, [filteredSections, openSections]);

  const toggleSection = (sectionId) => {
    setOpenSections((prev) => ({
      ...prev,
      [sectionId]: !prev[sectionId],
    }));
  };

  const toggleAllVisible = () => {
    if (allVisibleOpen) {
      setOpenSections((prev) => {
        const next = { ...prev };
        filteredSections.forEach((section) => {
          next[section.id] = false;
        });
        return next;
      });
      return;
    }

    setOpenSections((prev) => {
      const next = { ...prev };
      filteredSections.forEach((section) => {
        next[section.id] = true;
      });
      return next;
    });
  };

  return (
    <section className="space-y-6">
      <ArbitrageHero
        activeView={activeView}
        onChangeView={setActiveView}
        sectionsCount={ARBITRAGE_SECTIONS.length}
      />

      <div className="rounded-[30px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
        <div className="border-b border-slate-200 p-4 dark:border-slate-800 sm:p-5">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.22em] text-slate-400">
                {activeView === "cours" ? "Mode apprentissage" : "Mode QCM"}
              </p>

              <h2 className="mt-1 flex items-center gap-2 text-xl font-bold text-slate-950 dark:text-white">
                {activeView === "cours" ? (
                  <>
                    <FaBookOpen className="text-[#013E8E] dark:text-blue-300" />
                    Fiches d’arbitrage
                  </>
                ) : (
                  <>
                    <FaQuestionCircle className="text-[#4CA82F]" />
                    Quiz d’arbitrage
                  </>
                )}
              </h2>
            </div>

            {activeView === "cours" && (
              <button
                type="button"
                onClick={toggleAllVisible}
                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-slate-950 px-4 py-3 text-sm font-bold text-white transition hover:bg-[#013E8E] dark:bg-white dark:text-slate-950 dark:hover:bg-blue-100"
              >
                {allVisibleOpen ? <FaChevronUp /> : <FaChevronDown />}
                {allVisibleOpen ? "Tout replier" : "Tout déplier"}
              </button>
            )}
          </div>
        </div>

        {activeView === "cours" ? (
          <>
            <ArbitrageToolbar
              search={search}
              onSearchChange={setSearch}
              categories={ARBITRAGE_CATEGORIES}
              activeCategory={activeCategory}
              onCategoryChange={setActiveCategory}
              resultsCount={filteredSections.length}
            />

            <div className="space-y-5 p-4 sm:p-5">
              {filteredSections.length === 0 ? (
                <div className="rounded-3xl border border-dashed border-slate-300 bg-slate-50 p-10 text-center dark:border-slate-700 dark:bg-slate-800/40">
                  <p className="text-lg font-bold text-slate-900 dark:text-white">
                    Aucun résultat
                  </p>
                  <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                    Modifie ta recherche ou choisis un autre thème.
                  </p>
                </div>
              ) : (
                filteredSections.map((section) => (
                  <ArbitrageSectionCard
                    key={section.id}
                    section={section}
                    isOpen={!!openSections[section.id]}
                    onToggle={() => toggleSection(section.id)}
                  />
                ))
              )}
            </div>
          </>
        ) : (
          <div className="p-4 sm:p-5">
            <ArbitrageQuiz />
          </div>
        )}
      </div>
    </section>
  );
}